/**
 * @fileoverview Product-side tag editor (a field block in the ProductAdminPage details card).
 * @module features/tags/components/ProductTagsSection
 */
import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { CirclePlus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { TagChip } from './TagChip'
import {
  useTagsQuery,
  useProductTagsQuery,
  useSetProductTagsMutation,
  useCreateTagMutation,
} from '../api/tags'
import type { Tag } from '../schemas/tagSchema'

interface ProductTagsSectionProps {
  productId: string
}

/**
 * Self-saving multi-select tag editor for a single product.
 *
 * Reads the product's current tags and the full tag catalog, then persists any
 * add/remove as a full-replace `PUT /api/tags/products/{productId}`.
 *
 * @component
 */
export function ProductTagsSection({ productId }: ProductTagsSectionProps): ReactElement {
  const { t } = useTranslation('features-tags')
  const { data: allTags, isError: tagsError } = useTagsQuery()
  const { data: current, isLoading, isError: productTagsError } = useProductTagsQuery(productId)
  const setTags = useSetProductTagsMutation()
  const createTag = useCreateTagMutation()

  const currentTags = current ?? []

  const save = (tagUuids: string[]): void => {
    setTags.mutate(
      { productExternalId: productId, tagUuids },
      {
        onError: () =>
          toast.error(t('productSection.saveError', 'Failed to update product tags.')),
      }
    )
  }
  const remove = (uuid: string): void => {
    save(currentTags.filter(tag => tag.uuid !== uuid).map(tag => tag.uuid))
  }
  const add = (tag: Tag): void => {
    if (currentTags.some(existing => existing.uuid === tag.uuid)) return
    save([...currentTags.map(existing => existing.uuid), tag.uuid])
  }
  const createAndAdd = (name: string): void => {
    createTag.mutate(
      { name },
      {
        onSuccess: tag => add(tag),
        onError: () => toast.error(t('productSection.createError', 'Failed to create tag.')),
      }
    )
  }

  const available = (allTags ?? []).filter(
    tag => !currentTags.some(existing => existing.uuid === tag.uuid)
  )

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{t('productSection.heading', 'Tags')}</Label>
        <AddTagControl
          tags={available}
          existingNames={(allTags ?? []).map(tag => tag.name)}
          onAdd={add}
          onCreate={createAndAdd}
          disabled={setTags.isPending || createTag.isPending}
        />
      </div>
      {isLoading ? (
        <Skeleton className="h-9 w-full" />
      ) : productTagsError || tagsError ? (
        <p className="text-sm text-destructive">
          {t('productSection.loadError', 'Failed to load tags.')}
        </p>
      ) : (
        <div className="bg-input/30 border-input flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border px-3 py-1.5 shadow-xs">
          {currentTags.length === 0 && (
            <span className="text-sm text-muted-foreground">
              {t('productSection.empty', 'No tags applied yet.')}
            </span>
          )}
          {currentTags.map(tag => (
            <TagChip
              key={tag.uuid}
              tag={tag}
              onRemove={() => remove(tag.uuid)}
              removeDisabled={setTags.isPending}
              removeLabel={t('productSection.removeAria', {
                name: tag.name,
                defaultValue: 'Remove {{name}}',
              })}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface AddTagControlProps {
  tags: Tag[]
  /** Every catalog tag name (applied or not), used to suppress the quick-create item. */
  existingNames: string[]
  onAdd: (tag: Tag) => void
  onCreate: (name: string) => void
  disabled: boolean
}

/**
 * Popover + Command picker listing tags not yet applied to the product.
 * Typing a name that matches no catalog tag surfaces an `Add "<name>"`
 * quick-create item.
 */
function AddTagControl({
  tags,
  existingNames,
  onAdd,
  onCreate,
  disabled,
}: AddTagControlProps): ReactElement {
  const { t } = useTranslation('features-tags')
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const trimmedQuery = query.trim()
  const showCreate =
    trimmedQuery.length > 0 &&
    !existingNames.some(name => name.toLowerCase() === trimmedQuery.toLowerCase())

  const handleOpenChange = (next: boolean): void => {
    setOpen(next)
    if (!next) setQuery('')
  }
  const handleSelect = (tag: Tag): void => {
    onAdd(tag)
    handleOpenChange(false)
  }
  const handleCreate = (): void => {
    onCreate(trimmedQuery)
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-6 text-muted-foreground hover:text-foreground"
          aria-label={t('productSection.addTag', 'Add tag')}
          disabled={disabled}
        >
          <CirclePlus className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0">
        <Command>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('productSection.searchPlaceholder', 'Search tags...')}
          />
          <CommandList className="scrollbar-subtle">
            <CommandEmpty>{t('productSection.noResults', 'No tags found.')}</CommandEmpty>
            <CommandGroup>
              {tags.map(tag => (
                <CommandItem key={tag.uuid} value={tag.name} onSelect={() => handleSelect(tag)}>
                  <TagChip tag={tag} />
                </CommandItem>
              ))}
              {showCreate && (
                <CommandItem value={`__create__ ${trimmedQuery}`} onSelect={handleCreate}>
                  <CirclePlus className="size-4" />
                  {t('productSection.createTag', {
                    name: trimmedQuery,
                    defaultValue: 'Add "{{name}}"',
                  })}
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
